export type LetterGrade = 'A' | 'B' | 'C' | 'D' | 'F';

interface GradeBadgeProps {
  grade: LetterGrade | string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

const GRADE_CONFIG: Record<LetterGrade, { bg: string; text: string; border: string; label: string }> = {
  A: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-200',
    label: 'Excellent',
  },
  B: {
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    border: 'border-blue-200',
    label: 'Good',
  },
  C: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-200',
    label: 'Fair',
  },
  D: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-200',
    label: 'Poor',
  },
  F: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-200',
    label: 'Failing',
  },
};

const SIZE_CONFIG = {
  sm: {
    container: 'w-6 h-6',
    text: 'text-caption font-bold',
  },
  md: {
    container: 'w-8 h-8',
    text: 'text-body-sm font-bold',
  },
  lg: {
    container: 'w-10 h-10',
    text: 'text-body font-bold',
  },
} as const;

export function GradeBadge({ grade, size = 'md', showLabel = false }: GradeBadgeProps) {
  const normalizedGrade = (grade?.toUpperCase() as LetterGrade) || 'F';
  const config = GRADE_CONFIG[normalizedGrade] || GRADE_CONFIG.F;
  const sizeConfig = SIZE_CONFIG[size];

  return (
    <div className="inline-flex items-center gap-2">
      <span
        className={`inline-flex items-center justify-center ${sizeConfig.container} ${sizeConfig.text} rounded-full ${config.bg} ${config.text} border ${config.border}`}
        role="status"
        aria-label={`Grade ${normalizedGrade}: ${config.label}`}
      >
        {normalizedGrade}
      </span>
      {showLabel && (
        <span className={`text-body-sm ${config.text}`}>{config.label}</span>
      )}
    </div>
  );
}

interface ScoreGaugeProps {
  score: number;
  size?: number;
  showLabel?: boolean;
}

export function ScoreGauge({ score, size = 120, showLabel = true }: ScoreGaugeProps) {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  const getScoreColor = (s: number): string => {
    if (s >= 90) return 'text-status-success';
    if (s >= 50) return 'text-status-warning';
    return 'text-status-error';
  };

  const getScoreStrokeColor = (s: number): string => {
    if (s >= 90) return 'stroke-status-success';
    if (s >= 50) return 'stroke-status-warning';
    return 'stroke-status-error';
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="transform -rotate-90"
          role="img"
          aria-label={`Score: ${score} out of 100`}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-gray-light"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={`transition-all duration-500 ${getScoreStrokeColor(score)}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-heading-1 font-bold ${getScoreColor(score)}`}>
            {score}
          </span>
          <span className="text-caption text-gray-medium">/ 100</span>
        </div>
      </div>
      {showLabel && (
        <p className="text-body-sm text-gray-medium mt-3">Overall Score</p>
      )}
    </div>
  );
}

export function getGradeFromScore(score: number): LetterGrade {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

export function getGradeColor(grade: LetterGrade | string): string {
  const normalizedGrade = (grade?.toUpperCase() as LetterGrade) || 'F';
  return GRADE_CONFIG[normalizedGrade]?.text || GRADE_CONFIG.F.text;
}

export function getScoreColor(score: number): string {
  if (score >= 90) return 'text-status-success';
  if (score >= 70) return 'text-status-warning';
  if (score >= 50) return 'text-status-warning';
  return 'text-status-error';
}

export { GRADE_CONFIG };
